/**
 * EPD comparison helpers. Values from different associations or evaluation runs are never mixed
 * silently: comparisons group by association and say when as-of dates differ.
 */

export interface TraitValue { value: number; acc?: number; pct?: number }

export interface EvaluationSnapshot {
  event_id: string;
  association: string;
  kind: "epd" | "ebv" | "genomic";
  as_of: string | null;
  release?: string;
  source: "imported" | "entered";
  genomic?: boolean;
  traits: Record<string, TraitValue>;
  indexes: Record<string, TraitValue>;
}

export interface Target { trait: string; min?: number; max?: number }
export type TargetOutcome = "pass" | "fail" | "unknown";

export interface TargetResult { trait: string; outcome: TargetOutcome; value?: number; min?: number; max?: number }

/** Check each target against one snapshot. A missing trait is unknown, never a pass or a zero. */
export function checkTargets(snapshot: Pick<EvaluationSnapshot, "traits" | "indexes"> | null | undefined, targets: Target[]): TargetResult[] {
  return targets.map((t) => {
    const v = snapshot?.traits[t.trait] ?? snapshot?.indexes[t.trait];
    if (!v || typeof v.value !== "number") return { trait: t.trait, outcome: "unknown", min: t.min, max: t.max };
    const ok = (t.min === undefined || v.value >= t.min) && (t.max === undefined || v.value <= t.max);
    return { trait: t.trait, outcome: ok ? "pass" : "fail", value: v.value, min: t.min, max: t.max };
  });
}

/** Distinct runs among the snapshots being compared, so the table can warn when they differ. */
export function runsOf(snapshots: Array<Pick<EvaluationSnapshot, "association" | "as_of" | "release">>): Array<{ association: string; as_of: string | null; release?: string; count: number }> {
  const map = new Map<string, { association: string; as_of: string | null; release?: string; count: number }>();
  for (const s of snapshots) {
    const key = `${s.association}|${s.as_of ?? ""}|${s.release ?? ""}`;
    const cur = map.get(key);
    if (cur) cur.count++;
    else map.set(key, { association: s.association, as_of: s.as_of, release: s.release, count: 1 });
  }
  return [...map.values()].sort((a, b) => (b.as_of ?? "").localeCompare(a.as_of ?? ""));
}

/** Association names people type differently ("Red Angus", "RAAA") compare as one. */
export function associationKey(name: string): string {
  const k = name.trim().toLowerCase().replace(/[^a-z0-9]/g, "");
  const aliases: Record<string, string> = {
    raaa: "raaa", redangus: "raaa", redangusassociation: "raaa", redangusassociationofamerica: "raaa",
    aaa: "aaa", angus: "aaa", americanangus: "aaa", americanangusassociation: "aaa",
    aha: "aha", hereford: "aha", americanhereford: "aha", americanherefordassociation: "aha",
    asa: "asa", simmental: "asa", americansimmental: "asa", americansimmentalassociation: "asa",
    aica: "aica", charolais: "aica", americaninternationalcharolais: "aica",
  };
  return aliases[k] ?? k;
}

/** Short display name for the common associations; anything else is shown as written. */
export function associationShort(name: string): string {
  const k = associationKey(name);
  return ({ raaa: "RAAA", aaa: "AAA", aha: "AHA", asa: "ASA", aica: "AICA" } as Record<string, string>)[k] ?? name;
}

/** Width of a percentile bar: Top 1% fills it, Top 100% is empty. */
export function percentileFill(pct: number | undefined): number {
  if (pct === undefined || !Number.isFinite(pct)) return 0;
  return Math.max(0, Math.min(100, 101 - pct));
}

export function formatEpd(v: number): string {
  const s = Math.abs(v) >= 100 ? v.toFixed(0) : Math.abs(v) >= 10 ? v.toFixed(1).replace(/\.0$/, "") : v.toFixed(2).replace(/0$/, "").replace(/\.$/, "");
  return v > 0 ? `+${s}` : s;
}
